import { decidedByHr, mdy, RELATIONSHIP_LABEL, requestRef, VOTE_WINDOW_LABEL, type MyRequest } from "@scheduler/shared";

import { STATUS_LABEL, capacity, dateRange, hours } from "./format";

function statusDetail(r: MyRequest): string {
  if (r.status === "PENDING") {
    if (r.type === "FIELD") return r.projectManager ? `Waiting on ${r.projectManager.name}, the project's PM` : "Waiting on HR";
    if (decidedByHr(r.timeOffKind)) return "Waiting on HR";
    return r.waitingOn ? `Waiting on ${r.waitingOn.name}` : "No supervisor on record — HR can decide it";
  }
  if (r.status === "CANCELLED" && r.cancelledBy) return `by ${r.cancelledBy.name} ${capacity(r.cancelledAs)}`.trim();
  if (r.decidedBy) return `by ${r.decidedBy.name} ${capacity(r.decidedAs)}`.trim();
  return "";
}

/**
 * Everything the caller is on or created, including cancelled ones — the record is permanent.
 *
 * Change (punch list S35, Jeremy, 3 Nov 2026) sits beside Cancel on a pending or approved request
 * the API says may be changed: it opens the form filled in with that request, and the save
 * replaces it. A request made that way says what it replaces, which stays until it is approved.
 */
export function RequestList({ requests, meId, onCancel, onChange, changingId, busyId }: {
  requests: MyRequest[];
  meId: string;
  onCancel: (r: MyRequest) => void;
  onChange: (r: MyRequest) => void;
  /** The request the form is changing now, so its button shows pressed. */
  changingId: string | null;
  busyId: string | null;
}) {
  if (!requests.length) return <p className="muted">No requests yet.</p>;
  const byId = new Map(requests.map((r) => [r.id, r]));
  return (
    <div className="tablewrap">
      <table className="data">
        <thead>
          <tr>
            <th>Type</th><th>Who</th><th className="r">Dates</th><th className="r">Charged</th><th>Status</th><th />
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id}>
              {/* The label comes from the API: the kind, or "Time Off" to anyone outside the rule. */}
              <td><span className={`pill ${r.type}`}>{r.label}</span></td>
              <td>
                {r.people.map((p) => (p.id === meId ? "You" : p.name)).join(", ")}
                {r.title ? <span className="sub">{r.title}</span> : null}
                {r.project ? <span className="sub">{r.project.name}{r.projectManager ? ` · PM ${r.projectManager.name}` : " · no PM, HR decides"}</span> : null}
                {r.bereavementRelationship ? <span className="sub">{RELATIONSHIP_LABEL[r.bereavementRelationship]}</span> : null}
                {r.voteWindow ? <span className="sub">{VOTE_WINDOW_LABEL[r.voteWindow]}</span> : null}
                {r.summonsReceived ? <span className="sub">Summons received by HR</span> : null}
                {r.explanation ? <span className="sub explain">{r.explanation}</span> : null}
                {r.submittedBy.id !== meId ? <span className="sub">requested by {r.submittedBy.name}</span> : null}
                {/* The request this one replaces (rule 16), when it is in the list; Approvals' words. */}
                {r.supersedesRequestId && byId.has(r.supersedesRequestId) ? (() => {
                  const old = byId.get(r.supersedesRequestId)!;
                  return <span className="sub">replaces {requestRef(old.label, old.start, old.end)} ({old.status.toLowerCase()})</span>;
                })() : null}
              </td>
              <td className="r">{dateRange(r.start, r.end)}</td>
              <td className="r">{r.type === "PTO" ? `${hours(r.chargedHours)} h` : "—"}</td>
              <td>
                <span className={`pill ${r.status}`}>{STATUS_LABEL[r.status]}</span>
                <span className="sub">{statusDetail(r)}</span>
                {r.denialReason ? <span className="sub explain">Reason: {r.denialReason}</span> : null}
                {r.lockedThrough ? <span className="sub">Payroll processed through {mdy(r.lockedThrough)}. Contact HR to change it.</span> : null}
              </td>
              <td>
                <span className="acts-inline">
                  {r.canChange ? (
                    <button type="button" className="ghost" aria-pressed={changingId === r.id} disabled={busyId === r.id} onClick={() => onChange(r)}>
                      Change
                    </button>
                  ) : null}
                  {r.canCancel ? (
                    <button type="button" className="ghost" disabled={busyId === r.id} onClick={() => onCancel(r)}>
                      {busyId === r.id ? "Cancelling…" : "Cancel"}
                    </button>
                  ) : null}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
